/**
 * Design tokens — single source of truth for colour and typography (PRI-23).
 *
 * The app previously carried ~250 arbitrary font sizes (`text-[10px]`…
 * `text-[14px]`) and 3400+ hardcoded hex literals, with the dark theme
 * duplicated inline as `isDark ? "#0f1923" : "hsl(var(--accent))"` across
 * 16 files. Everything visual now resolves through these tokens or the CSS
 * variables in globals.css, so the theme is defined once and the user can
 * scale it (see DENSITY_PRESETS).
 *
 * Colour rule: never write a hex literal in a component. Use a Tailwind
 * class backed by a CSS variable (`bg-card`, `text-muted-foreground`) or,
 * for lead status, the LEAD_STATUS_TOKENS map below.
 */

/* ── Typography ────────────────────────────────────────────────────────── */

/**
 * Five semantic steps, ratio ≈1.2, with an absolute 13px floor.
 *
 * Five rather than seven on purpose: 11/12/13px tokens are visually
 * indistinguishable and would recreate the arbitrary-size problem under a
 * nicer name. Extra hierarchy comes from weight, muted colour, uppercase
 * and letter-spacing — never from smaller text.
 */
export const TYPE_SCALE = {
  display: { size: 30, leading: 1.2 },
  title: { size: 22, leading: 1.3 },
  heading: { size: 18, leading: 1.4 },
  body: { size: 15, leading: 1.5 },
  label: { size: 13, leading: 1.4 },
} as const;

export type TypeScaleToken = keyof typeof TYPE_SCALE;

/** Nothing in the app renders below this, in any density preset. */
export const MIN_FONT_SIZE_PX = 13;

/* ── Density presets ───────────────────────────────────────────────────── */

/**
 * `fontScale` never drops below 1.00. "Compact" compresses *spacing*, not
 * text — someone who wants more leads on screen shouldn't have to squint.
 * `densityScale` applies to padding, gaps and row heights, never font-size.
 */
export const DENSITY_PRESETS = {
  compact: { label: "Compacto", fontScale: 1.0, densityScale: 0.88 },
  normal: { label: "Normal", fontScale: 1.0, densityScale: 1.0 },
  comfortable: { label: "Confortável", fontScale: 1.1, densityScale: 1.06 },
  large: { label: "Grande", fontScale: 1.25, densityScale: 1.12 },
} as const;

export type DensityPreset = keyof typeof DENSITY_PRESETS;

export const DEFAULT_DENSITY: DensityPreset = "normal";

export const DENSITY_ORDER: DensityPreset[] = [
  "compact",
  "normal",
  "comfortable",
  "large",
];

export function isDensityPreset(value: unknown): value is DensityPreset {
  return typeof value === "string" && value in DENSITY_PRESETS;
}

/** Key used for the localStorage mirror of the server-side preference. */
export const DENSITY_STORAGE_KEY = "zunox_density";

/* ── Lead status colours ───────────────────────────────────────────────── */

/**
 * One colour identity per lead status, as Tailwind classes backed by CSS
 * variables (defined in globals.css) so light/dark resolve automatically.
 *
 * This is the colour half of the status map; the label/icon half lives in
 * LEAD_STATUS_META (lib/design/lead-status.ts). Components render
 * <LeadStatusBadge />, which combines the two — reach for this map directly
 * only when a badge is the wrong shape (a row tint, a chart legend).
 *
 * Covers all 14 lead statuses, not just the 7 the
 * design brief listed — migrating a lead list against a 7-entry palette
 * would collapse the other 7 into the neutral fallback and lose colour
 * information the UI has today. PRI-47 is expected to reconcile this list
 * down; until it does, this mirrors reality.
 */
export const LEAD_STATUS_TOKENS = {
  nova: {
    bg: "bg-status-nova-bg",
    fg: "text-status-nova-fg",
    border: "border-status-nova-border",
  },
  "follow-up": {
    bg: "bg-status-follow-up-bg",
    fg: "text-status-follow-up-fg",
    border: "border-status-follow-up-border",
  },
  qualificacao: {
    bg: "bg-status-qualificacao-bg",
    fg: "text-status-qualificacao-fg",
    border: "border-status-qualificacao-border",
  },
  interessado: {
    bg: "bg-status-interessado-bg",
    fg: "text-status-interessado-fg",
    border: "border-status-interessado-border",
  },
  qualificada: {
    bg: "bg-status-qualificada-bg",
    fg: "text-status-qualificada-fg",
    border: "border-status-qualificada-border",
  },
  "aguarda-documentacao": {
    bg: "bg-status-aguarda-documentacao-bg",
    fg: "text-status-aguarda-documentacao-fg",
    border: "border-status-aguarda-documentacao-border",
  },
  "documentacao-incompleta": {
    bg: "bg-status-documentacao-incompleta-bg",
    fg: "text-status-documentacao-incompleta-fg",
    border: "border-status-documentacao-incompleta-border",
  },
  "em-analise": {
    bg: "bg-status-em-analise-bg",
    fg: "text-status-em-analise-fg",
    border: "border-status-em-analise-border",
  },
  "proposta-aprovada": {
    bg: "bg-status-proposta-aprovada-bg",
    fg: "text-status-proposta-aprovada-fg",
    border: "border-status-proposta-aprovada-border",
  },
  "assinatura-digital": {
    bg: "bg-status-assinatura-digital-bg",
    fg: "text-status-assinatura-digital-fg",
    border: "border-status-assinatura-digital-border",
  },
  "assinatura-presencial": {
    bg: "bg-status-assinatura-presencial-bg",
    fg: "text-status-assinatura-presencial-fg",
    border: "border-status-assinatura-presencial-border",
  },
  "em-conferencia": {
    bg: "bg-status-em-conferencia-bg",
    fg: "text-status-em-conferencia-fg",
    border: "border-status-em-conferencia-border",
  },
  operacoes: {
    bg: "bg-status-operacoes-bg",
    fg: "text-status-operacoes-fg",
    border: "border-status-operacoes-border",
  },
  condicionada: {
    bg: "bg-status-condicionada-bg",
    fg: "text-status-condicionada-fg",
    border: "border-status-condicionada-border",
  },
  financiada: {
    bg: "bg-status-financiada-bg",
    fg: "text-status-financiada-fg",
    border: "border-status-financiada-border",
  },
  encerrada: {
    bg: "bg-status-encerrada-bg",
    fg: "text-status-encerrada-fg",
    border: "border-status-encerrada-border",
  },
} as const;

export type LeadStatusToken = keyof typeof LEAD_STATUS_TOKENS;

export function isLeadStatusToken(value: unknown): value is LeadStatusToken {
  return typeof value === "string" && value in LEAD_STATUS_TOKENS;
}

/**
 * Colour classes for a lead status, falling back to neutral tokens for
 * statuses not in the canonical set (legacy values, or new ones added
 * before this map is updated) so an unknown status renders readably
 * instead of unstyled.
 */
export function leadStatusTokens(status: string | null | undefined) {
  if (isLeadStatusToken(status)) return LEAD_STATUS_TOKENS[status];
  return {
    bg: "bg-muted",
    fg: "text-muted-foreground",
    border: "border-border",
  } as const;
}

/* ── Validade de uma certidão auto ─────────────────────────────────────── */

/**
 * Os quatro estados de validade de uma certidão auto, em tokens.
 *
 * ─── A decisão que importa: EXPIRADA É CALADA ──────────────────────────────
 *
 * O mapeamento ingénuo pintava a expirada de vermelho, e seria errado. A folha
 * de origem começa em Junho de 2020 e tem 2.904 linhas: depois de a importar,
 * a esmagadora maioria do registo está expirada — e isso é o NORMAL, não uma
 * avaria. Vermelho em 95% das linhas é uma parede vermelha, e uma parede
 * vermelha não diz nada: o olho deixa de a ler e salta-a, incluindo nas poucas
 * linhas em que ela tinha algo a dizer.
 *
 * É o mesmo argumento que a `Repeticoes.matricula` já faz do outro lado — «um
 * aviso que dispara quase sempre deixa de ser lido». Por isso a cor gasta-se
 * no que é ACCIONÁVEL:
 *
 *   - `a_expirar` leva o âmbar, porque é a única que pede acção hoje;
 *   - `valida` leva o verde, que é barato porque são poucas;
 *   - `expirada` não leva cor nenhuma — é o fundo do registo, não um alarme;
 *   - `desconhecida` idem, e distingue-se pelo rótulo («Sem data»), que é o
 *     que diz o que fazer. A fila dela é a do `a_confirmar`.
 *
 * Nenhuma usa `--destructive`: nada aqui está errado. Uma certidão expirada
 * cumpriu o que tinha a cumprir.
 */
export const CERTIDAO_VALIDADE_TOKENS = {
  valida: {
    bg: "bg-ok-bg",
    fg: "text-ok-fg",
    border: "border-ok-border",
  },
  a_expirar: {
    bg: "bg-warn-bg",
    fg: "text-warn-fg",
    border: "border-warn-border",
  },
  expirada: {
    bg: "bg-muted",
    fg: "text-muted-foreground",
    border: "border-border",
  },
  desconhecida: {
    bg: "bg-muted",
    fg: "text-muted-foreground",
    border: "border-border",
  },
} as const;
